import { MAIL_BLOCKED_ACCOUNTS_ENV } from '@electron/shared/mail-accounts.types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { electronApis } from '@/features/electron-api';
import {
  AXON_MCP_SERVERS_QUERY_KEY,
  useMcpServersQuery,
  useUpdateMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';
import { inBrowser } from '@/utils/capabilities';

import type { ServerEntry, ServerEntryInput } from '@littlebird/axon-mcp';

export const MAIL_ACCOUNTS_QUERY_KEY = [
  'integration-configuration',
  'apple-mail',
  'accounts',
] as const;

function normalize(id: string): string {
  return id.trim().toUpperCase();
}

function transportEnv(server: ServerEntry): Record<string, string> {
  return server.transport.kind === 'http' ? {} : { ...server.transport.env };
}

function isValidAccountId(id: string): boolean {
  return /^[A-F0-9-]+$/.test(id);
}

export function blockedAccountIds(server: ServerEntry): Set<string> {
  const raw = transportEnv(server)[MAIL_BLOCKED_ACCOUNTS_ENV] ?? '';
  return new Set(
    raw
      .split(',')
      .map(normalize)
      .filter((id) => id && isValidAccountId(id))
  );
}

export function withAccountEnabled(
  server: ServerEntry,
  accountId: string,
  enabled: boolean
): Partial<ServerEntryInput> {
  if (server.transport.kind === 'http') return {};
  const blocked = blockedAccountIds(server);
  if (enabled) blocked.delete(normalize(accountId));
  else blocked.add(normalize(accountId));

  const env = Object.fromEntries(
    Object.entries(transportEnv(server)).filter(
      ([key]) => key !== MAIL_BLOCKED_ACCOUNTS_ENV
    )
  );
  if (blocked.size > 0) {
    env[MAIL_BLOCKED_ACCOUNTS_ENV] = [...blocked].join(',');
  }
  const { env: _oldEnv, ...transport } = server.transport;
  return {
    transport: Object.keys(env).length > 0 ? { ...transport, env } : transport,
  };
}

export function useMailAccountsQuery(
  serverSlug: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: [...MAIL_ACCOUNTS_QUERY_KEY, serverSlug],
    queryFn: () => electronApis.axonMcp.listMailAccounts(serverSlug ?? ''),
    // listing may launch Mail and prompt for Automation; once per app session
    staleTime: Infinity,
    retry: false,
    enabled: enabled && serverSlug !== undefined && !inBrowser(),
  });
}

export function useMailAccountToggle(serverSlug: string | undefined) {
  const serversQuery = useMcpServersQuery();
  const update = useUpdateMcpServerMutation();
  const queryClient = useQueryClient();

  const server = serversQuery.data?.find((entry) => entry.slug === serverSlug);

  const setEnabled = (accountId: string, enabled: boolean) => {
    // the latest cached entry, so a toggle never drops one saved just before it
    const latest = queryClient
      .getQueryData<ServerEntry[]>(AXON_MCP_SERVERS_QUERY_KEY)
      ?.find((entry) => entry.slug === serverSlug);
    if (!latest || latest.transport.kind === 'http') return;
    update.mutate({
      slug: latest.slug,
      changes: withAccountEnabled(latest, accountId, enabled),
    });
  };

  return {
    blockedIds: server ? blockedAccountIds(server) : new Set<string>(),
    saving: update.isPending,
    setEnabled,
  };
}
